// FILE: src/services/weeklyTrendService.js
//
// Derives the "+X pts vs last week" trend from readiness_snapshots history.
// Compares the newest snapshot to the one closest to 7 days older.

/**
 * @param {Array<{ score: number, computed_at: string }>} snapshots
 *        Snapshot rows newest-first, as returned by loadReadinessSnapshots().
 * @param {Date} [now]
 * @returns {{
 *   delta: number|null,         // points (signed, integer rounded)
 *   direction: 'up'|'down'|'flat'|null,
 *   sufficient: boolean,        // whether there were enough snapshots
 *   baselineScore: number|null, // ~7-day-ago score used for the comparison
 *   latestScore:   number|null,
 * }}
 */
export function computeWeeklyTrend(snapshots, now = new Date()) {
  if (!Array.isArray(snapshots) || snapshots.length < 2) {
    return { delta: null, direction: null, sufficient: false, baselineScore: null, latestScore: null };
  }

  const sorted = [...snapshots].sort((a, b) => new Date(b.computed_at) - new Date(a.computed_at));
  const latest = sorted[0];
  if (typeof latest?.score !== 'number') {
    return { delta: null, direction: null, sufficient: false, baselineScore: null, latestScore: null };
  }

  // Find the snapshot closest to 7 days before the latest snapshot.
  const target = new Date(latest.computed_at).getTime() - 7 * 86400000;
  let baseline = null;
  let bestDistance = Infinity;
  for (let i = 1; i < sorted.length; i += 1) {
    const t = new Date(sorted[i].computed_at).getTime();
    const distance = Math.abs(t - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      baseline = sorted[i];
    }
  }

  if (!baseline || typeof baseline.score !== 'number') {
    return {
      delta: null, direction: null, sufficient: false,
      baselineScore: null, latestScore: latest.score,
    };
  }

  const rawDelta = latest.score - baseline.score;
  const delta = Math.round(rawDelta * 10) / 10;
  let direction = 'flat';
  if (delta > 0.5) direction = 'up';
  else if (delta < -0.5) direction = 'down';

  return {
    delta,
    direction,
    sufficient: true,
    baselineScore: baseline.score,
    latestScore: latest.score,
  };
}
